import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  BarChart3, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Package, 
  ListChecks,
  TrendingUp,
  Activity,
  ArrowRight,
  FileText
} from "lucide-react";

export default function AdminDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [recentTests, setRecentTests] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, testsRes, productsRes] = await Promise.all([
          axios.get(`${API}/analytics`, { headers }),
          axios.get(`${API}/tests`, { headers }),
          axios.get(`${API}/products`, { headers })
        ]);
        setAnalytics(analyticsRes.data);
        setRecentTests(testsRes.data.slice(0, 5));
        setProducts(productsRes.data);
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [headers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "passed": return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "failed": return <XCircle className="w-4 h-4 text-red-500" />;
      case "in_progress": return <Clock className="w-4 h-4 text-amber-500" />;
      default: return <AlertTriangle className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      passed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
      failed: "bg-red-500/10 text-red-600 border-red-500/20",
      in_progress: "bg-amber-500/10 text-amber-600 border-amber-500/20"
    };
    return (
      <Badge variant="outline" className={variants[status] || "bg-slate-500/10"}>
        {status.replace("_", " ")}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Tests</CardTitle>
            <Activity className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono">{analytics?.total_tests || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">All time verification tests</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Passed</CardTitle>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-emerald-600">{analytics?.passed_tests || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Successfully verified</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
            <XCircle className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-red-600">{analytics?.failed_tests || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Requires attention</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pass Rate</CardTitle>
            <TrendingUp className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-blue-600">{analytics?.pass_rate || 0}%</div>
            <p className="text-xs text-muted-foreground mt-1">Overall success rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Tests */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Tests</CardTitle>
              <CardDescription>Latest verification sessions</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/reports")} data-testid="view-all-reports-btn">
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentTests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tests yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTests.map((test) => (
                  <div
                    key={test.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/tester/verify/${test.id}`)}
                    data-testid={`test-item-${test.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(test.status)}
                      <div>
                        <p className="font-medium text-sm">{test.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          SN: <span className="font-mono">{test.serial_number || "N/A"}</span> • {test.tester_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-muted-foreground">
                          {new Date(test.started_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs font-mono text-muted-foreground">
                          {test.step_results ? test.step_results.filter(sr => ["pass","fail","warning","skipped"].includes(sr.status)).length : test.current_step}/{test.total_steps} steps
                        </p>
                      </div>
                      {getStatusBadge(test.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions & Products */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/admin/products")} data-testid="manage-products-btn">
                <Package className="w-4 h-4 mr-2" />
                Manage Products
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/admin/steps")} data-testid="manage-steps-btn">
                <ListChecks className="w-4 h-4 mr-2" />
                Verification Steps
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/admin/analytics")} data-testid="view-analytics-btn">
                <BarChart3 className="w-4 h-4 mr-2" />
                View Analytics
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Products</CardTitle>
              <CardDescription>{products.length} registered</CardDescription>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <p className="text-sm text-muted-foreground">No products yet</p>
              ) : (
                <div className="space-y-2">
                  {products.slice(0, 3).map((product) => (
                    <div key={product.id} className="p-2 rounded border bg-muted/30">
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.product_type}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Failure Analysis */}
      {analytics?.failure_by_step?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Common Failure Points</CardTitle>
            <CardDescription>Steps with the most failures</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.failure_by_step.slice(0, 5).map((item, index) => (
                <div key={`fail-${item.step}-${index}`} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-600 font-mono text-sm">
                    {item.failures}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.step}</p>
                    <div className="h-2 bg-muted rounded-full mt-1 overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full"
                        style={{ width: `${Math.min(item.failures * 10, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
